/**
 * 근로자의 날(5/1) 수당 카드 — PayrollTab 안에서 5월에만 노출.
 * 설계문서: required/근로자의날.md §10.1
 *
 * 직원별로:
 *  - breakdown 미니 요약 (유급/실근무/가산)
 *  - 시급제 결근 시 잠재 유급분 안내 말풍선
 *  - 합산 토글
 */

import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Switch,
  Button,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import type { MonthlyPayrollData } from '../../types/employee';
import LaborDayInfoSheet from './LaborDayInfoSheet';

interface LaborDayAllowanceCardProps {
  monthlyData: MonthlyPayrollData[];
  /** (employeeId, included) → 토글 변경 콜백 */
  onToggle: (employeeId: string, included: boolean) => void | Promise<void>;
  /** 사용자가 비로그인/무료라 Firestore 동기화가 안 되는 경우 안내 */
  syncDisabled?: boolean;
}

const formatNumber = (n: number) => n.toLocaleString('ko-KR');

const LaborDayAllowanceCard: React.FC<LaborDayAllowanceCardProps> = ({
  monthlyData,
  onToggle,
  syncDisabled,
}) => {
  const [infoOpen, setInfoOpen] = useState(false);

  const rows = monthlyData.filter((d) => d.laborDayBreakdown != null);
  if (rows.length === 0) return null;

  return (
    <>
      <Accordion
        defaultExpanded
        sx={{
          mb: 2,
          borderRadius: 1,
          border: '1px solid var(--border-default)',
          background: '#fff',
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary expandIcon={<Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{GLYPHS.down}</Box>}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                근로자의 날 수당 (5/1)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                직원별로 합산 여부를 선택할 수 있습니다.
              </Typography>
            </Box>
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setInfoOpen(true);
              }}
            >
              계산 근거
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {syncDisabled && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              ※ 비로그인/무료 사용자: 이 토글은 이 기기에만 저장됩니다.
            </Typography>
          )}
          <Stack spacing={1.5}>
            {rows.map((row) => {
              const breakdown = row.laborDayBreakdown!;
              const included = row.laborDayIncluded === true;
              const isAbsenceTipVisible = breakdown.isHourly && !breakdown.worked;
              return (
                <Box
                  key={row.employee.id}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    background: included ? 'background.paper' : 'grey.50',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {row.employee.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={breakdown.isHourly ? '시급제' : '월급제'}
                      color={breakdown.isHourly ? 'primary' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    <Chip
                      size="small"
                      label={breakdown.is5Plus ? '5인 이상' : '5인 미만'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    <Chip
                      size="small"
                      label={breakdown.worked ? `근무 ${breakdown.workedHours}h` : '결근'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Switch
                      checked={included}
                      onChange={(e) => onToggle(row.employee.id, e.target.checked)}
                      inputProps={{ 'aria-label': `${row.employee.name} 노동절 합산 토글` }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                      gap: 1,
                    }}
                  >
                    <BreakdownCell label="유급분" value={breakdown.paidHolidayPay} />
                    <BreakdownCell label="실근무분" value={breakdown.workPay} />
                    <BreakdownCell label="가산 (50%)" value={breakdown.premiumPay} />
                    <BreakdownCell
                      label="합계 (잠재)"
                      value={breakdown.total}
                      strong
                      muted={!included}
                    />
                  </Box>

                  {isAbsenceTipVisible && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: 'warning.50',
                        border: '1px dashed',
                        borderColor: 'warning.300',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'warning.dark' }}>
                        💡 시급제 직원은 결근해도 노동절 8시간분(
                        {formatNumber(breakdown.paidHolidayPay)})이 유급으로 발생합니다.
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {included ? '월 총급여에 합산' : '합산 제외 (월 총급여에 미반영)'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: included ? 'primary.main' : 'text.disabled',
                      }}
                    >
                      {included ? '+' : ''}
                      {formatNumber(included ? row.laborDayAllowance ?? 0 : 0)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <LaborDayInfoSheet open={infoOpen} onClose={() => setInfoOpen(false)} />
    </>
  );
};

interface BreakdownCellProps {
  label: string;
  value: number;
  strong?: boolean;
  muted?: boolean;
}
const BreakdownCell: React.FC<BreakdownCellProps> = ({ label, value, strong, muted }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: strong ? 700 : 500,
        color: muted ? 'text.disabled' : 'text.primary',
      }}
     className="num">{value > 0 ? formatNumber(value) : '0'}</Typography>
  </Box>
);

export default LaborDayAllowanceCard;
